import React, { useMemo } from 'react';
import { Avatar, Button, Empty, Tooltip, Typography } from 'antd';
import { DownOutlined, DownloadOutlined, MessageOutlined, RobotOutlined } from '@ant-design/icons';
import moment from 'moment';
import TeamAssistantMessageList from './TeamAssistantMessageList';
import TeamAssistantChatMessagesSkeleton from './TeamAssistantChatMessagesSkeleton';
import TeamAssistantComposer from './TeamAssistantComposer';
import TeamAssistantCatalogActionCard from './TeamAssistantCatalogActionCard';
import { displayName, initials, roleTag } from './teamAssistantChatUtils';
import { useTeamAssistantChat } from '../../hooks/useTeamAssistantChat';
import './team-assistant.css';
import './team-assistant-chatgpt-shared.css';

const { Title, Text } = Typography;

/**
 * @param {'full' | 'embedded' | 'compact'} variant
 */
const TeamAssistantChat = ({
  currentUser,
  enabled = true,
  variant = 'full',
  className = '',
  title = 'Shared team conversation',
  subtitle,
  showHeader = true,
  messagesMaxHeight,
  features,
  style,
  size = 'default',
  browse = {},
  browseActive = false,
  headerExtra,
  viewingStaffUser = null,
  onClearStaffFilter,
  scrollResetKey = null,
}) => {
  const staffBrowseActive = browse?.userId != null;

  const isPageLayout = className.includes('team-assistant-root--page');
  const chat = useTeamAssistantChat(currentUser, { enabled, browse, scrollResetKey });
  const isMini = size === 'mini';

  const rootClass = [
    'team-assistant-root',
    `team-assistant-root--${variant}`,
    isMini ? 'team-assistant-root--mini' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const messagesStyle = useMemo(() => {
    if (!messagesMaxHeight) return undefined;
    return { maxHeight: messagesMaxHeight };
  }, [messagesMaxHeight]);

  const defaultSubtitle =
    'Search carriers, read full company profiles, and (admins) propose new companies with confirm. Uses team uploads and catalog data.';

  const showWelcomeEmpty =
    !browseActive && chat.messages.length === 0 && !chat.loading;
  const showMessagesSkeleton =
    chat.loading && chat.messages.length === 0 && !browseActive;
  const showBrowseEmpty =
    browseActive &&
    chat.messages.length === 0 &&
    !chat.loading &&
    !chat.isFetching;
  const browseEmptyLabel = (browse?.q || browse?.keyword || '').trim()
    ? 'Search not found.'
    : 'No messages match your filters.';

  const browseSearchHighlight = (browse?.q || browse?.keyword || '').trim();

  const renderMessageHeader = (m) => {
    if (m.role === 'assistant') {
      if (isPageLayout) {
        return null;
      }
      const askedBy = m.triggered_by?.name;
      return (
        <div className="team-assistant-msg-head">
          <Avatar size="small" icon={<RobotOutlined />} className="team-assistant-avatar assistant" />
          <div>
            <Text strong>Assistant</Text>
            {askedBy && (
              <Text type="secondary" className="team-assistant-sub">
                {' '}
                · reply to {askedBy}
              </Text>
            )}
            {m.created_at && (
              <Text type="secondary" className="team-assistant-time">
                {moment(m.created_at).format('MMM D, h:mm A')}
              </Text>
            )}
          </div>
        </div>
      );
    }

    const person = m.user || { name: 'Team member' };
    return (
      <div className="team-assistant-msg-head">
        <Avatar size="small" className="team-assistant-avatar user">
          {initials(person.name)}
        </Avatar>
        <div>
          <Text strong>{displayName(person)}</Text>
          {roleTag(person.role)}
          {m.created_at && (
            <Text type="secondary" className="team-assistant-time">
              {moment(m.created_at).format('MMM D, h:mm A')}
            </Text>
          )}
        </div>
      </div>
    );
  };

  const compact = variant === 'compact' || isMini || isPageLayout;
  const handleSend = () => {
    if (staffBrowseActive && onClearStaffFilter) {
      onClearStaffFilter();
    }
    chat.handleSend();
  };

  const composerFeatures = isMini
    ? { footnote: false, catalogTags: false, ...features }
    : isPageLayout
      ? { footnote: false, singleLineComposer: true, ...features }
      : features;

  return (
    <div className={rootClass} style={style}>
      <div className="team-assistant-chat team-assistant-chat-full">
        {showHeader && !isMini && (
          <header className="team-assistant-chat-header team-assistant-chat-header--toolbar">
            <div className="team-assistant-chat-header-main">
              {isPageLayout ? (
                <h2 className="team-assistant-page-chat-title">
                  <MessageOutlined aria-hidden /> {title}
                </h2>
              ) : (
                <Title level={4} style={{ marginTop: 0, marginBottom: 0 }}>
                  <MessageOutlined /> {title}
                </Title>
              )}
              {!isPageLayout && (subtitle ?? defaultSubtitle) && (
                <Text type="secondary" className="team-assistant-chat-subtitle">
                  {subtitle ?? defaultSubtitle}
                </Text>
              )}
            </div>
            <div className="team-assistant-chat-header-actions">
              <Tooltip title="Export visible chat as Markdown">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={chat.handleExportTranscript}
                  disabled={chat.messages.length === 0}
                  aria-label="Export chat"
                />
              </Tooltip>
              {headerExtra}
            </div>
          </header>
        )}

        <div className="team-assistant-messages-host">
          <div
            className={`team-assistant-messages${isPageLayout ? ' team-assistant-messages--page-layout' : ''}`}
            ref={chat.messagesScrollRef}
            style={messagesStyle}
          >
            <div className="team-assistant-messages-inner">
            {chat.hasOlder && (
              <div className="team-assistant-load-older">
                <Button
                  type="link"
                  size="small"
                  loading={chat.isLoadingOlder}
                  onClick={chat.handleLoadOlder}
                >
                  Load older messages
                </Button>
                {chat.totalCount > chat.messages.length && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Showing {chat.messages.length} of {chat.totalCount}
                  </Text>
                )}
              </div>
            )}
            {chat.messages.length > 0 ? (
              <TeamAssistantMessageList
                messages={chat.messages}
                streaming={chat.streaming}
                renderMessageHeader={renderMessageHeader}
                highlightQuery={browseSearchHighlight}
                onAskAgain={chat.handleAskAgain}
                onRegenerate={chat.handleRegenerate}
              />
            ) : showMessagesSkeleton ? (
              <TeamAssistantChatMessagesSkeleton />
            ) : showWelcomeEmpty ? (
              <Empty
                className="team-assistant-welcome-empty"
                description="How can I help you today?"
              />
            ) : showBrowseEmpty ? (
              <div className="team-assistant-messages-empty-browse" role="status">
                <Text type="secondary">{browseEmptyLabel}</Text>
              </div>
            ) : null}
            {chat.catalogActions?.length > 0 && (
              <div className="team-assistant-catalog-actions">
                {chat.catalogActions.map((action) => (
                  <TeamAssistantCatalogActionCard
                    key={action.action_id}
                    action={action}
                    onDismiss={chat.dismissCatalogAction}
                    onConfirmed={() => chat.invalidateTeamAssistant?.()}
                  />
                ))}
              </div>
            )}
            <div ref={chat.messagesEndRef} className="team-assistant-messages-scroll-anchor" aria-hidden />
            </div>
          </div>
        </div>

        {staffBrowseActive && (
          <div className="team-assistant-staff-browse-hint" role="status">
            <Text type="secondary" className="team-assistant-staff-browse-hint-text">
              Browsing <Text strong>{viewingStaffUser?.name || 'team member'}</Text>
              &apos;s history · New messages send as <Text strong>{chat.me.name}</Text>
              {onClearStaffFilter && (
                <>
                  {' · '}
                  <Button
                    type="link"
                    htmlType="button"
                    className="team-assistant-staff-browse-hint-clear"
                    onClick={onClearStaffFilter}
                  >
                    Clear team member filter
                  </Button>
                </>
              )}
            </Text>
          </div>
        )}
        {chat.showScrollToBottom && chat.messages.length > 0 && (
          <Button
            type="default"
            shape="circle"
            size="large"
            className="team-assistant-scroll-to-bottom"
            icon={<DownOutlined />}
            aria-label="Scroll to latest messages"
            onClick={() => chat.scrollToBottom({ force: true })}
          />
        )}
        <TeamAssistantComposer
          me={chat.me}
          draft={chat.draft}
          onDraftChange={chat.syncDraftFromComposer}
          attachments={chat.attachments}
          removeAttachment={chat.removeAttachment}
          hasUploadingAttachments={chat.hasUploadingAttachments}
          readyAttachments={chat.readyAttachments}
          listening={chat.listening}
          streaming={chat.streaming}
          useWebSearch={chat.useWebSearch}
          setUseWebSearch={chat.setUseWebSearch}
          onSend={handleSend}
          onVoiceToggle={chat.handleVoiceToggle}
          onAttachUpload={chat.handleAttachUpload}
          onStopStream={chat.handleStopStream}
          compact={compact}
          features={composerFeatures}
        />
      </div>
    </div>
  );
};

export default TeamAssistantChat;
